import { queryOptions } from '@tanstack/react-query'
import { apiClient } from '~/lib/client'

import { handleApiError, handleResponse } from '~/utils/response'

import { ReminderKeys } from '~/features/auth/api/keys'
import { ActiveMedicationsResponse, GetActiveMedicationsResponse, GetCurrentProcessingResponse, GetMedicalPaymentHistoryResponse, GetMedicationsSummaryResponse, GetNotificationReminderValueResponse, GetPersonalMedicalResponse, GetPrescriptionMedicationResponse, GetRefillResponse, medicalsMedicationsQueryFilterOptions, PastMedicationsResponse, PendingMedicationsResponse, ProcessingMedicationsResponse, RefillPrescriptionHistoryResponse } from '../types'
import { medicationsKeys } from './keys'
import { formatRefillDate, formatRefillDateAbbreviated } from '@workspace/lib/date'
import { mockGetCurrentProcessingResponse, mockMedicalHistoryResponse } from './mock'
import { buildQueryString } from '@workspace/lib/url'

export const medicalsMedicationQueryOptions = (id1: string, id2: string) => {
    return queryOptions({
        queryKey: medicationsKeys.medication(id1, id2),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/${id1}/${id2}`);

                const result = await handleResponse<GetPersonalMedicalResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },

    });
};

export const activeMedicationsQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.activeMedications(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/active`);

                const result = await handleResponse<GetActiveMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
    });
};

export const activeMedicalsMedicationsQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsWithFilter(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications${buildQueryString(filter)}`);

                const result = await handleResponse<ActiveMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(response) {
            console.log("medicalsMedicationsQueryOptions", { response })
            return response?.data;
        }
    });
};

export const pendingMedicalsMedicationsQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsWithFilter(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications${buildQueryString(filter)}`);

                const result = await handleResponse<PendingMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(response) {
            if (!response?.data) return [];

            const refills = response?.data?.refills?.map((item) => {
                return {
                    prescription_id: item.prescription_id,
                    refill_id: item.refill_id,
                    patient_id: item.patient_id,
                    prescribed_on: item.request_date,
                    status: item.payment_status,
                    payment_status: item.payment_status,
                    cost: item.medication.cost,
                    medication: {
                        name: item.medication.name,
                        dosage: item.medication.dosage,
                        duration: item.medication.duration,
                        cost: item.medication.cost,
                        medication_id: item.medication.medication_id,
                        instructions: item.medication.instructions,
                        notes: item.medication.notes,
                    },
                    instructions: item.medication.instructions,
                    notes: item.medication.notes,
                }
            });

            const prescriptions = response?.data?.prescriptions?.map((item) => {
                return {
                    prescription_id: item.prescription_id,
                    patient_id: item.patient_id,
                    prescribed_on: item.prescribed_on,
                    status: item.status,
                    payment_status: item.payment_status,
                    cost: item.cost,
                    medication: {
                        name: item.medications[0].name,
                        dosage: item.medications[0].dosage,
                        duration: item.medications[0].duration,
                        cost: item.medications[0].cost,
                        medication_id: item.medications[0].medication_id,
                        instructions: item.medications[0].instructions,
                        notes: item.medications[0].notes,
                    },
                    instructions: item.medications[0].instructions,
                }
            })

            return [...prescriptions, ...refills];
        }
    });
};

export const pastMedicalsMedicationsQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsWithFilter(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications${buildQueryString(filter)}`);

                const result = await handleResponse<PastMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(data) {
            return data;
        }
    });
};

export const processingMedicalsMedicationsQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsWithFilter(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications${buildQueryString(filter)}`);

                const result = await handleResponse<ProcessingMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(response) {
            if (!response?.data) return [];

            const transformed = response?.data?.map((item) => ({
                ...item,
                name: item.medication,
                delivery_date: formatRefillDate(item.delivery_date),
                request_date: formatRefillDate(item.requested_on),
                progress: [
                    {
                        status: item.requested_on ? 'completed' : 'pending',
                        label: item.requested_on ? 'Requested' : 'Pending',
                        date: item.requested_on !== "Unavailable" ? formatRefillDateAbbreviated(item.requested_on) : "Unavailable"
                    },
                    {
                        status: item.approved_on ? 'completed' : 'pending',
                        label: item.approved_on ? 'Approved' : 'Pending',
                        date: item.approved_on !== "Unavailable" ? formatRefillDateAbbreviated(item.approved_on) : "Unavailable"
                    },
                    {
                        status: item.order_placed_on !== "Unavailable" ? 'completed' : 'pending',
                        label: item.order_placed_on !== "Unavailable" ? 'Placed' : 'Pending',
                        date: item.order_placed_on !== "Unavailable" ? formatRefillDateAbbreviated(item.order_placed_on) : "Unavailable"
                    },
                    {
                        status: item.delivered_on !== "Unavailable" ? 'completed' : 'pending',
                        label: item.delivered_on !== "Unavailable" ? 'Delivered' : 'Pending',
                        date: item.delivered_on !== "Unavailable" ? formatRefillDateAbbreviated(item.delivered_on) : "Unavailable"
                    },
                ]
            }));

            return transformed;

        }
    });
};


export const medicalsMedicationsSummaryQueryOptions = (program: 'weight_loss' | 'urgent_care') => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsSummary(program),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/summary?program=${program}`);

                const result = await handleResponse<GetMedicationsSummaryResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(data) {
            return data?.summary;
        }
    });
};

export const pastMedicationsQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.pastMedications(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/active`);

                const result = await handleResponse<GetActiveMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select() {
            return [
                {
                    "prescription_id": "68371a1ed4617ef87557fe0c",
                    "patient_id": "680e6b827bf7945af112a58b",
                    "admin_id": "680e56d7d7323a3e2d0cafd2",
                    "refilled_on": null,
                    "status": "active",
                    "prescribed_on": "2025-05-28T01:00:00+01:00",
                    "payment_status": "paid",
                    "paid_on": "2025-05-30",
                    "cost": 400,
                    "created_at": 1748441630.1784945,
                    "delivery_status": "pending",
                    "delivery_date": "2025-05-31T01:00:00+01:00",
                    "delivered_on": null,
                    "medication_id": "68371a1ed4617ef87557fe0d",
                    "medication": {
                        "name": "Metformin",
                        "dosage": "500mg",
                        "quantity": 0,
                        "duration": "30 days",
                        "cost": 400,
                        "medication_id": "68371a1ed4617ef87557fe0d",
                        "frequency": "daily",
                        "medication_cost": 400,
                    },
                    "is_due": true,
                    "due_date": "2025-06-27"
                },
                {
                    "prescription_id": "68371a1ed4617ef87557fe0c",
                    "patient_id": "680e6b827bf7945af112a58b",
                    "admin_id": "680e56d7d7323a3e2d0cafd2",
                    "refilled_on": null,
                    "status": "rejected",
                    "prescribed_on": "2025-05-28T01:00:00+01:00",
                    "payment_status": "paid",
                    "paid_on": "2025-05-30",
                    "cost": 400,
                    "created_at": 1748441630.1784945,
                    "delivery_status": "pending",
                    "delivery_date": "2025-05-31T01:00:00+01:00",
                    "delivered_on": null,
                    "medication_id": "68371a1ed4617ef87557fe0d",
                    "medication": {
                        "name": "Metformin",
                        "dosage": "500mg",
                        "quantity": 0,
                        "duration": "30 days",
                        "cost": 400,
                        "medication_id": "68371a1ed4617ef87557fe0d",
                        "frequency": "daily",
                        "medication_cost": 400,
                    },
                    "is_due": false,
                    "due_date": "2025-06-27"
                },
                {
                    "prescription_id": "68371a1ed4617ef87557fe0c",
                    "patient_id": "680e6b827bf7945af112a58b",
                    "admin_id": "680e56d7d7323a3e2d0cafd2",
                    "refilled_on": null,
                    "status": "requested",
                    "prescribed_on": "2025-05-28T01:00:00+01:00",
                    "payment_status": "paid",
                    "paid_on": "2025-05-30",
                    "cost": 400,
                    "created_at": 1748441630.1784945,
                    "delivery_status": "pending",
                    "delivery_date": "2025-05-31T01:00:00+01:00",
                    "delivered_on": null,
                    "medication_id": "68371a1ed4617ef87557fe0d",
                    "medication": {
                        "name": "Metformin",
                        "dosage": "500mg",
                        "quantity": 0,
                        "duration": "30 days",
                        "cost": 400,
                        "medication_id": "68371a1ed4617ef87557fe0d",
                        "frequency": "daily",
                        "medication_cost": 400,
                    },
                    "is_due": false,
                    "due_date": "2025-06-27"
                }
            ]
        }
    });
};

export const pendingPaymentsQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.pendingPayments(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/active`);

                const result = await handleResponse<GetActiveMedicationsResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select() {
            return [
                {
                    "prescription_id": "68371a1ed4617ef87557fe0c",
                    "patient_id": "680e6b827bf7945af112a58b",
                    "admin_id": "680e56d7d7323a3e2d0cafd2",
                    "refilled_on": null,
                    "status": "active",
                    "prescribed_on": "2025-05-28T01:00:00+01:00",
                    "payment_status": "paid",
                    "paid_on": "2025-05-30",
                    "cost": 400,
                    "created_at": 1748441630.1784945,
                    "delivery_status": "pending",
                    "delivery_date": "2025-05-31T01:00:00+01:00",
                    "delivered_on": null,
                    "medication_id": "68371a1ed4617ef87557fe0d",
                    "medication": {
                        "name": "Metformin",
                        "dosage": "500mg",
                        "quantity": 0,
                        "duration": "30 days",
                        "cost": 400,
                        "medication_id": "68371a1ed4617ef87557fe0d",
                        "frequency": "daily",
                        "medication_cost": 400,
                    },
                    "is_due": true,
                    "due_date": "2025-06-27"
                },
                {
                    "prescription_id": "68371a1ed4617ef87557fe0c",
                    "patient_id": "680e6b827bf7945af112a58b",
                    "admin_id": "680e56d7d7323a3e2d0cafd2",
                    "refilled_on": null,
                    "status": "active",
                    "prescribed_on": "2025-05-28T01:00:00+01:00",
                    "payment_status": "paid",
                    "paid_on": "2025-05-30",
                    "cost": 400,
                    "created_at": 1748441630.1784945,
                    "delivery_status": "pending",
                    "delivery_date": "2025-05-31T01:00:00+01:00",
                    "delivered_on": null,
                    "medication_id": "68371a1ed4617ef87557fe0d",
                    "medication": {
                        "name": "Metformin",
                        "dosage": "500mg",
                        "quantity": 0,
                        "duration": "30 days",
                        "cost": 400,
                        "medication_id": "68371a1ed4617ef87557fe0d",
                        "frequency": "daily",
                        "medication_cost": 400,
                    },
                    "is_due": true,
                    "due_date": "2025-06-27"
                }
            ]
        }
    });
};

export const medicalsMedicationsRefillHistoryQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsRefillHistory(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/history`);

                const result = await handleResponse<RefillPrescriptionHistoryResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select() {
            return mockMedicalHistoryResponse;
        }

    });
};

export const medicalsMedicationsHistoryQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.medicationsHistory(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/history${buildQueryString(filter)}`);

                const result = await handleResponse<RefillPrescriptionHistoryResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(response) {
            return response?.medication_history;
            // return mockMedicalHistoryResponse;
        }

    });
};

export const medicalsMedicationsPaymentHistoryQueryOptions = (filter: medicalsMedicationsQueryFilterOptions) => {
    return queryOptions({
        queryKey: medicationsKeys.paymentHistory(filter),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/user/payment-history`);

                const result = await handleResponse<GetMedicalPaymentHistoryResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select(response) {
            console.log("payment history", { response })
            return response;
        }
    });
};

export const medicalsMedicationsRefillProcessingQueryOptions = () => {
    return queryOptions({
        queryKey: medicationsKeys.refillProcessing(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/processing`);

                const result = await handleResponse<GetCurrentProcessingResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        select() {
            // TODO: remove this mock data
            const data = mockGetCurrentProcessingResponse;
            const processingRefills = data?.processing_medications?.processing_refills;
            const processingMedications = data?.processing_medications?.processing_medications;

            const transformedRefills = processingRefills?.map((refill) => ({
                ...refill,
                name: refill.medication.name,
                delivery_date: formatRefillDate(refill.delivery_date),
                request_date: formatRefillDate(refill.request_date),
                progress: [
                    {
                        status: 'completed',
                        label: 'Requested',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'completed',
                        label: 'Approved',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'pending',
                        label: 'Placed',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'pending',
                        label: 'Delivered',
                        date: formatRefillDateAbbreviated(refill.delivery_date)
                    },
                ]
            }));

            const transformedMedications = processingMedications?.map((refill) => ({
                ...refill,
                name: refill.medication.name,
                delivery_date: formatRefillDate(refill.delivery_date),
                request_date: formatRefillDate(refill.request_date),
                progress: [
                    {
                        status: 'completed',
                        label: 'Requested',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'completed',
                        label: 'Approved',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'pending',
                        label: 'Placed',
                        date: formatRefillDateAbbreviated(refill.request_date)
                    },
                    {
                        status: 'pending',
                        label: 'Delivered',
                        date: formatRefillDateAbbreviated(refill.delivery_date)
                    },
                ]
            }));
            // merge the two arrays
            const merged = [...transformedRefills, ...transformedMedications];

            return merged;

            // old code, don't remove
            // const processingRefills = data?.processing_medications?.processing_refills
            // const processingMedications = data?.processing_medications?.processing_medications

            // console.log("processingRefills", processingRefills)
            // console.log("processingMedications", processingMedications)

            // return processingRefills?.map((refill) => ({
            //     ...refill,
            //     name: refill.medication.name,
            //     delivery_date: formatRefillDate(refill.delivery_date),
            //     request_date: formatRefillDate(refill.request_date),
            //     progress: [
            //         {
            //             status: 'completed',
            //             label: 'Requested',
            //             date: formatRefillDateAbbreviated(refill.request_date)
            //         },
            //         {
            //             status: 'completed',
            //             label: 'Approved',
            //             date: formatRefillDateAbbreviated(refill.request_date)
            //         },
            //         {
            //             status: 'pending',
            //             label: 'Order Placed',
            //             date: formatRefillDateAbbreviated(refill.request_date)
            //         },
            //         {
            //             status: 'pending',
            //             label: 'Delivered',
            //             date: formatRefillDateAbbreviated(refill.delivery_date)
            //         },
            //     ]
            // }))
        },

    });
};

export const getPrescriptionMedicationQueryOptions = (id?: string) => {
    return queryOptions({
        queryKey: medicationsKeys.prescription(id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/prescriptions/${id}`);

                const result = await handleResponse<GetPersonalMedicalResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!id,
    });
};

export const getMedicationRefillQueryOptions = (id?: string) => {
    return queryOptions({
        queryKey: medicationsKeys.refill(id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/refill/${id}`);

                const result = await handleResponse<GetRefillResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!id,
    });
};

export const getPrescriptionMedicationByMedicationIdQueryOptions = (prescriptionId: string, medicationId: string) => {
    return queryOptions({
        queryKey: medicationsKeys.prescriptionMedication(prescriptionId, medicationId),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/medicals/medications/${prescriptionId}/${medicationId}`);

                const result = await handleResponse<GetPrescriptionMedicationResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },
        enabled: !!prescriptionId && !!medicationId,
    });
};

export const notificationReminderValues = () => {
    return queryOptions({
        queryKey: ReminderKeys.settings(),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/users/settings/reminder`);

                const result = await handleResponse<GetNotificationReminderValueResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }
                return result.data;
            } catch (error) {
                handleApiError(error);
            }
        },

    });
};

